import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, ArrowRight, AlertTriangle, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

const P  = '#C29500';
const P2 = '#E8C040';

const KudiLogo = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="ai-g" x1="0" y1="0" x2="80" y2="80" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor={P2}/><stop offset="100%" stopColor={P}/>
      </linearGradient>
    </defs>
    <rect width="80" height="80" rx="20" fill="url(#ai-g)"/>
    <rect x="20" y="18" width="9" height="44" rx="4.5" fill="#fff"/>
    <path d="M29 40 L54 20" stroke="#fff" strokeWidth="9" strokeLinecap="round"/>
    <path d="M29 40 L54 60" stroke="#fff" strokeWidth="9" strokeLinecap="round"/>
    <path d="M42 56 L54 60 L50 48" stroke="#fff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default function AcceptInvite() {
  const [params]  = useSearchParams();
  const token     = params.get('token') || '';
  const navigate  = useNavigate();
  const [info, setInfo]   = useState(null);
  const [error, setError] = useState('');
  const [pw, setPw]       = useState('');
  const [pw2, setPw2]     = useState('');
  const [show, setShow]   = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setError('Missing invite token.'); return; }
    api.get(`/team/invite-info/${token}`)
      .then(r => setInfo(r.data))
      .catch(err => setError(err?.response?.data?.detail || 'This invite is invalid or expired.'));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error('Password must be at least 8 characters');
    if (pw !== pw2)    return toast.error('Passwords do not match');
    setLoading(true);
    try {
      const res = await api.post('/team/accept', { token, password: pw });
      localStorage.setItem('access_token', res.data.access_token);
      localStorage.setItem('refresh_token', res.data.refresh_token);
      toast.success('Welcome to the team!');
      setTimeout(() => { window.location.href = '/dashboard'; }, 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not accept invite');
    } finally { setLoading(false); }
  };

  const shell = (child) => (
    <div style={{ minHeight: '100vh', background: '#0A0A0F', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: 'Inter, sans-serif' }}>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
            <KudiLogo size={40}/>
            <span style={{ fontSize: 26, fontWeight: 800, color: '#fff' }}>Kudi<span style={{ color: P2 }}>bot</span></span>
          </Link>
        </div>
        <div style={{ background: '#111', border: '1px solid #1a1a1a', borderRadius: 16, padding: 28 }}>
          {child}
        </div>
      </motion.div>
    </div>
  );

  if (error) return shell(
    <div style={{ textAlign: 'center' }}>
      <AlertTriangle size={36} style={{ color: '#EF4444', margin: '0 auto 12px' }}/>
      <h2 style={{ color: '#fff', fontSize: 18, margin: '0 0 8px' }}>Invite unavailable</h2>
      <p style={{ color: '#64748B', fontSize: 14, margin: '0 0 18px' }}>{error}</p>
      <Link to="/login" style={{ color: P2, fontWeight: 700, textDecoration: 'none' }}>Go to sign in →</Link>
    </div>
  );

  if (!info) return shell(<p style={{ color: '#64748B', textAlign: 'center' }}>Loading invite…</p>);

  const inputStyle = {
    width: '100%', background: '#0D1117', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12, padding: '13px 44px 13px 42px', color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box',
  };

  return shell(
    <>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <Users size={28} style={{ color: P2, margin: '0 auto 10px' }}/>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: '0 0 6px' }}>Join {info.business}</h1>
        <p style={{ color: '#64748B', fontSize: 13, margin: 0 }}>
          You've been invited as <b style={{ color: P2 }}>{info.role_label}</b>. Set a password to join.
        </p>
      </div>

      <div style={{ background: '#0D1117', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#94A3B8' }}>
        {info.email}
      </div>

      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ position: 'relative' }}>
          <Lock size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#475569' }}/>
          <input type={show ? 'text' : 'password'} placeholder="Create a password" value={pw} onChange={e => setPw(e.target.value)} required style={inputStyle}/>
          <button type="button" onClick={() => setShow(!show)} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#475569', padding: 0 }}>
            {show ? <EyeOff size={15}/> : <Eye size={15}/>}
          </button>
        </div>
        <div style={{ position: 'relative' }}>
          <Lock size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#475569' }}/>
          <input type={show ? 'text' : 'password'} placeholder="Confirm password" value={pw2} onChange={e => setPw2(e.target.value)} required style={inputStyle}/>
        </div>
        <motion.button whileTap={{ scale: 0.98 }} type="submit" disabled={loading}
          style={{ width: '100%', padding: '14px 0', borderRadius: 12, border: 'none', cursor: 'pointer',
            background: `linear-gradient(135deg, ${P2}, ${P})`, color: '#1A1300', fontWeight: 800, fontSize: 15,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: loading ? 0.6 : 1 }}>
          {loading ? 'Joining…' : <>Join Team <ArrowRight size={16}/></>}
        </motion.button>
      </form>
    </>
  );
}
